import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { InputControl, SelectControl } from "@aurem/components";
import { Button, Text, Toast } from "@aurem/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { Play } from "lucide-react";
import {
  useConfigWorkflows,
  WORKFLOWS as WORKFLOWS_QUERY,
} from "pages/Controls/Workflow/New/hooks/use-workflow.hooks";
import { useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  FeeWorkflowKey,
  gql,
  TriggerType,
  WorkflowKey,
  WorkflowType,
} from "types";
import { z } from "zod";
import { DevToolbarCard } from "../components/DevToolbarCard";

const PAYROLL_DEDUCTION_WORKFLOW_NAME = "Payroll deduction for savings";

const PROCESS_EMPLOYEE_TERMINATIONS = gql(/* GraphQL */ `
  mutation DevToolbarProcessEmployeeTerminations {
    processEmployeeTerminations
  }
`);

export const TRIGGER_WORKFLOW = gql(/* GraphQL */ `
  mutation TriggerWorkflow($workflowVersionId: String!, $context: JSON) {
    runWorkflow(workflowVersionId: $workflowVersionId, context: $context) {
      id
    }
  }
`);

export const TRIGGER_WORKFLOW_BY_KEY = gql(/* GraphQL */ `
  mutation TriggerWorkflowByKey($workflowKey: WorkflowKey!, $context: JSON) {
    runWorkflowByKey(workflowKey: $workflowKey, context: $context) {
      id
    }
  }
`);

const WORKFLOW_CONFIG_ENABLED = gql(/* GraphQL */ `
  query DevToolbarWorkflowConfigEnabled {
    workflowConfigEnabled
  }
`);

const TRIGGER_FEE_WORKFLOW = gql(/* GraphQL */ `
  mutation DevToolbarTriggerFeeWorkflow($workflowDefinitionId: String!) {
    triggerFeeWorkflow(workflowDefinitionId: $workflowDefinitionId)
  }
`);

const TRIGGER_FEE_WORKFLOW_BY_KEY = gql(/* GraphQL */ `
  mutation DevToolbarTriggerFeeWorkflowByKey($workflowKey: FeeWorkflowKey!) {
    triggerFeeWorkflowByKey(workflowKey: $workflowKey)
  }
`);

const workflowSchema = z.object({
  productInstanceId: z.string().min(1, "Required"),
  workflowDefinitionId: z.string(),
});

type WorkflowFormData = z.infer<typeof workflowSchema>;

const feeWorkflowSchema = z.object({
  workflowKey: z.string(),
  workflowDefinitionId: z.string(),
});

type FeeWorkflowFormData = z.infer<typeof feeWorkflowSchema>;

const ProcessTerminatedEmployeesCard = () => {
  const [processTerminations, { loading }] = useMutation(
    PROCESS_EMPLOYEE_TERMINATIONS,
  );

  const handleClick = useCallback(async () => {
    try {
      await processTerminations();
      Toast.success({
        title: "Terminated employees processing started",
      });
    } catch (error) {
      Toast.error({
        title: "Failed to process terminated employees",
        text: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }, [processTerminations]);

  return (
    <DevToolbarCard
      title="Process Terminated Employees"
      testId="devtoolbar-process-terminated-employees"
    >
      <div className="flex flex-col gap-2">
        <Text size="xs" className="text-gray500">
          Run the daily termination job for employees whose end date is today or
          earlier.
        </Text>
        <div className="flex justify-end">
          <Button
            size="xs"
            loading={loading}
            onClick={handleClick}
            startIcon={<Play size={12} />}
            data-e2e-id="devtoolbar-process-terminated-employees-run"
          >
            Run
          </Button>
        </div>
      </div>
    </DevToolbarCard>
  );
};

const TriggerWorkflowCard = () => {
  const [triggerWorkflow, { loading }] = useMutation(TRIGGER_WORKFLOW);
  const [triggerWorkflowByKey, { loading: byKeyLoading }] = useMutation(
    TRIGGER_WORKFLOW_BY_KEY,
  );

  const { data: configData } = useQuery(WORKFLOW_CONFIG_ENABLED);
  const configEnabled = configData?.workflowConfigEnabled ?? false;

  const { configWorkflows } = useConfigWorkflows({ skip: !configEnabled });
  const workflowKeyOptions = useMemo(
    () =>
      configWorkflows
        .filter(
          (wf) =>
            wf.workflowType === WorkflowType.Product &&
            wf.trigger.type !== TriggerType.Hook,
        )
        .map((wf) => ({ id: wf.key, text: wf.name })),
    [configWorkflows],
  );

  const [fetchWorkflows, { loading: workflowsLoading }] =
    useLazyQuery(WORKFLOWS_QUERY);

  const form = useForm<WorkflowFormData>({
    resolver: zodResolver(workflowSchema),
    defaultValues: {
      productInstanceId: "",
      workflowDefinitionId: "",
    },
  });

  const { handleSubmit, reset } = form;

  const runFromConfig = useCallback(
    async (input: WorkflowFormData) => {
      const workflowKey = (input.workflowDefinitionId ||
        WorkflowKey.PayrollDeductionForSavings) as WorkflowKey;

      await triggerWorkflowByKey({
        variables: {
          workflowKey,
          context: { productInstanceId: input.productInstanceId },
        },
      });
    },
    [triggerWorkflowByKey],
  );

  const runFromDb = useCallback(
    async (input: WorkflowFormData) => {
      const { data } = await fetchWorkflows();

      const workflowVersionId =
        data?.workflows.find((workflow) =>
          input.workflowDefinitionId
            ? workflow.id === input.workflowDefinitionId
            : workflow.name === PAYROLL_DEDUCTION_WORKFLOW_NAME,
        )?.currentVersionId ?? "";

      if (!workflowVersionId) {
        Toast.error({
          title: "Workflow version not found",
          text: `No current version found for workflow definition "${input.workflowDefinitionId}"`,
        });
        return;
      }

      await triggerWorkflow({
        variables: {
          workflowVersionId,
          context: { productInstanceId: input.productInstanceId },
        },
      });
    },
    [fetchWorkflows, triggerWorkflow],
  );

  const onSubmit = useCallback(
    async (input: WorkflowFormData) => {
      try {
        if (configEnabled) {
          await runFromConfig(input);
        } else {
          await runFromDb(input);
        }
        Toast.success({ title: "Workflow run successfully" });
        reset();
      } catch (error) {
        Toast.error({
          title: "Failed to run workflow",
          text: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },
    [configEnabled, runFromConfig, runFromDb, reset],
  );

  return (
    <DevToolbarCard
      title="Run Product Workflow"
      testId="devtoolbar-run-workflow"
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="flex flex-col gap-2"
        >
          <InputControl
            name="productInstanceId"
            size="sm"
            placeholder="Product Instance ID"
            data-e2e-id="devtoolbar-run-workflow-product-instance-id"
          />
          {configEnabled ? (
            <SelectControl
              name="workflowDefinitionId"
              size="sm"
              options={workflowKeyOptions}
              placeholder="Workflow config (Payroll if empty)"
              data-e2e-id="devtoolbar-run-workflow-workflow-definition-id"
            />
          ) : (
            <InputControl
              name="workflowDefinitionId"
              size="sm"
              placeholder="Workflow ID (Payroll workflow if left empty)"
              data-e2e-id="devtoolbar-run-workflow-workflow-definition-id"
            />
          )}
          <div className="flex justify-end">
            <Button
              type="submit"
              size="xs"
              loading={loading || byKeyLoading || workflowsLoading}
              startIcon={<Play size={12} />}
              data-e2e-id="devtoolbar-run-workflow-run"
            >
              Run
            </Button>
          </div>
        </form>
      </FormProvider>
    </DevToolbarCard>
  );
};

const TriggerFeeWorkflowCard = () => {
  const [triggerFeeWorkflow, { loading }] = useMutation(TRIGGER_FEE_WORKFLOW);
  const [triggerFeeWorkflowByKey, { loading: byKeyLoading }] = useMutation(
    TRIGGER_FEE_WORKFLOW_BY_KEY,
  );

  const { data: configData } = useQuery(WORKFLOW_CONFIG_ENABLED);
  const configEnabled = configData?.workflowConfigEnabled ?? false;

  const { configWorkflows } = useConfigWorkflows({ skip: !configEnabled });
  const feeWorkflowKeyOptions = useMemo(
    () =>
      configWorkflows
        .filter((wf) => wf.workflowType === WorkflowType.Fee)
        .map((wf) => ({
          id: wf.key,
          text: wf.name,
          dataAttributes: {
            "data-e2e-id": `devtoolbar-trigger-fee-workflow-key-option-${wf.key}`,
          },
        })),
    [configWorkflows],
  );

  const form = useForm<FeeWorkflowFormData>({
    resolver: zodResolver(feeWorkflowSchema),
    defaultValues: { workflowKey: "", workflowDefinitionId: "" },
  });

  const { handleSubmit, reset } = form;

  const onSubmit = useCallback(
    async (input: FeeWorkflowFormData) => {
      try {
        let runIds: string[] = [];

        if (configEnabled) {
          if (!input.workflowKey) {
            Toast.error({ title: "Select a fee workflow" });
            return;
          }
          const result = await triggerFeeWorkflowByKey({
            variables: { workflowKey: input.workflowKey as FeeWorkflowKey },
          });
          runIds = result.data?.triggerFeeWorkflowByKey ?? [];
        } else {
          if (!input.workflowDefinitionId) {
            Toast.error({ title: "Fee Workflow Definition ID is required" });
            return;
          }
          const result = await triggerFeeWorkflow({
            variables: { workflowDefinitionId: input.workflowDefinitionId },
          });
          runIds = result.data?.triggerFeeWorkflow ?? [];
        }

        Toast.success({
          title: `Fee workflow triggered (${runIds.length} run${runIds.length !== 1 ? "s" : ""})`,
        });
        reset();
      } catch (error) {
        Toast.error({
          title: "Failed to trigger fee workflow",
          text: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },
    [configEnabled, triggerFeeWorkflow, triggerFeeWorkflowByKey, reset],
  );

  return (
    <DevToolbarCard
      title="Run Fee Workflow"
      testId="devtoolbar-trigger-fee-workflow"
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="flex flex-col gap-2"
        >
          {configEnabled ? (
            <SelectControl
              name="workflowKey"
              size="sm"
              options={feeWorkflowKeyOptions}
              placeholder="Fee workflow"
              data-e2e-id="devtoolbar-trigger-fee-workflow-key"
            />
          ) : (
            <InputControl
              name="workflowDefinitionId"
              size="sm"
              placeholder="Fee Workflow Definition ID"
              data-e2e-id="devtoolbar-trigger-fee-workflow-definition-id"
            />
          )}
          <div className="flex justify-end">
            <Button
              type="submit"
              size="xs"
              loading={loading || byKeyLoading}
              startIcon={<Play size={12} />}
              data-e2e-id="devtoolbar-trigger-fee-workflow-run"
            >
              Run
            </Button>
          </div>
        </form>
      </FormProvider>
    </DevToolbarCard>
  );
};

export const TemporalTab = () => {
  return (
    <div
      className="px-4 py-3 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3"
      data-e2e-id="devtoolbar-temporal-tab"
    >
      <TriggerWorkflowCard />
      <TriggerFeeWorkflowCard />
      <ProcessTerminatedEmployeesCard />
    </div>
  );
};
